import React, { FC } from 'react';
import { ButtonWithIconAndLoader } from '@td-client-frontend/common';
import clsx from 'clsx';
import { useDeleteMarketingProfile, ProfileType } from '../../../hooks/useMarketingProfile';
import Modal from '../../Modal/NewModal';
import { AffectedGroups } from './AffectedGroups';
import { MarketingProfileById } from '../../../@types/Marketing';
import { Button } from '../../Button';

interface DeleteProfileModalBody {
  id: string;
  name: string;
  isBaseProfile: boolean;
  affectedGroups: number;
}

interface DeleteModalProps {
  profileData: MarketingProfileById;
  isOpen: boolean;
  setOpen: (x: boolean) => void;
  onDelete?: () => void;
}

const DeleteProfileModalBody: FC<DeleteProfileModalBody> = ({ id, name, isBaseProfile, affectedGroups }) => {
  return (
    <>
      {!isBaseProfile && !affectedGroups && (
        <p className="font-effra text-lg text-gray-45">Are you sure you want to delete the following profile?</p>
      )}
      <h6 className="font-effra text-xl text-gray-45">
        Profile:<span className="ml-3 text-2xl font-bold break-all">{name}</span>
      </h6>
      {isBaseProfile && (
        <p className="font-effra text-lg text-gray-45 mt-8">
          This profile cannot be deleted because it is set as a base profile. You will need to remove this setting first
          in order to delete it.
        </p>
      )}
      {!!affectedGroups && (
        <>
          <p className="font-effra text-lg text-gray-45 mt-8">
            This profile cannot be deleted because it is assigned to the following groups
          </p>
          <AffectedGroups profileName={name} id={id} type={ProfileType.PROFILE} hideDescription />
        </>
      )}
    </>
  );
};

const DeleteProfileModal: FC<DeleteModalProps> = ({ profileData, isOpen, setOpen, onDelete }) => {
  const { default: isBaseProfile, id, name, affectedOrganizationsCount } = profileData;

  const { deleteMarketingProfile, isLoading } = useDeleteMarketingProfile();

  const onClose = () => setOpen(false);

  const onProfileDelete = async () => {
    if (!isBaseProfile && !affectedOrganizationsCount && id) {
      await deleteMarketingProfile(id);
      onDelete?.();
    }
    onClose();
  };

  const CustomButtons = () => (
    <div className="flex gap-4">
      <Button title="Cancel" kind="secondary" onClick={onClose} />
      {!(isBaseProfile || affectedOrganizationsCount) && (
        <ButtonWithIconAndLoader
          title="Delete"
          loading={isLoading}
          className="flex items-center justify-center font-effra btn min-w-12"
          labelClasses={clsx('text-normal', isLoading && 'hidden')}
          onClick={onProfileDelete}
        />
      )}
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header="Delete profile"
      size="default"
      customFooterActions={{
        right: <CustomButtons />
      }}
    >
      <div className="p-6 pb-20" data-testid="delete-profile-modal">
        <DeleteProfileModalBody
          id={id}
          name={name}
          isBaseProfile={isBaseProfile}
          affectedGroups={affectedOrganizationsCount}
        />
      </div>
    </Modal>
  );
};

export default DeleteProfileModal;
